import { __decorate, __metadata } from "tslib";
import { throttle } from '@1inch-community/core/decorators';
import { lazyAppContextConsumer, lazyConsumer } from '@1inch-community/core/lazy';
import { dispatchEvent, observe, subscribe, translate } from '@1inch-community/core/lit-utils';
import { getRandomBrightColor } from '@1inch-community/core/theme';
import { ChainId, OverlayViewMode } from '@1inch-community/models';
import '@1inch-community/ui-components/button';
import '@1inch-community/ui-components/icon';
import { scrollContext } from '@1inch-community/ui-components/scroll';
import { html, LitElement } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { when } from 'lit/directives/when.js';
import { combineLatest, defer, filter, tap } from 'rxjs';
import '../../../../shared-elements/balance-view';
import { DisconnectEventModel } from '../../../disconnect/disconnect-event-model';
import { walletAccountContext } from '../../context';
import '../../i18n';
import '../wallet-account-card-account-more';
import { walletAccountCardStyle } from './wallet-account-card.style';
var MenuItemIds;
(function (MenuItemIds) {
    MenuItemIds[MenuItemIds["CopyAddress"] = 0] = "CopyAddress";
    MenuItemIds[MenuItemIds["ExternalView"] = 1] = "ExternalView";
    MenuItemIds[MenuItemIds["Switch"] = 2] = "Switch";
    MenuItemIds[MenuItemIds["Disconnect"] = 3] = "Disconnect";
})(MenuItemIds || (MenuItemIds = {}));
let WalletAccountCardElement = class WalletAccountCardElement extends LitElement {
    constructor() {
        super(...arguments);
        this.applicationContext = lazyAppContextConsumer(this);
        this.context = lazyConsumer(this, { context: walletAccountContext });
        this.isCollapsed = false;
        this.overlayId = null;
        this.menuItems = [
            {
                text: 'widgets.wallet-account-view.card.more.copy',
                style: 'standard',
                icon: 'copy16',
                id: MenuItemIds.CopyAddress,
            },
            {
                text: 'widgets.wallet-account-view.card.more.view',
                style: 'standard',
                icon: 'externalLink16',
                id: MenuItemIds.ExternalView,
            },
            {
                text: 'widgets.wallet-account-view.card.more.switch',
                style: 'standard',
                icon: 'swap24',
                id: MenuItemIds.Switch,
            },
            {
                text: 'widgets.wallet-account-view.card.more.disconnect',
                style: 'dangerous',
                icon: 'logout16',
                id: MenuItemIds.Disconnect,
            },
        ];
        this.scrollConsumer = lazyConsumer(this, { context: scrollContext });
        this.chainListView$ = defer(() => this.context.value.chainFilter$);
    }
    static { this.tagName = 'inch-wallet-account-card'; }
    static { this.styles = [walletAccountCardStyle]; }
    firstUpdated() {
        if (!this.context) {
            throw new Error('setup context before');
        }
        const collapseThreshold = 100;
        subscribe(this, this.scrollConsumer.value.scrollTopFromConsumer$.pipe(tap((value) => {
            this.isCollapsed = value > collapseThreshold;
            if (this.isCollapsed) {
                this.closeMenuMore();
            }
        })));
        subscribe(this, [
            combineLatest([
                this.context.value.connectedWalletAddress$,
                this.context.value.connectedWalletInfo$,
                this.context.value.chainId$,
            ]).pipe(filter(([address, info, chainId]) => address !== null && info !== null && chainId !== null), tap(([address, info, chainId]) => {
                this.walletAddress = address;
                this.walletInfo = info;
                this.chainId = chainId;
            })),
        ], { requestUpdate: false });
    }
    onChangeWalletClick() {
        dispatchEvent(this, 'changeWalletClick', undefined);
    }
    onMenuItemClick(id) {
        if (!this.context || !this.walletAddress || !this.chainId || !this.walletInfo) {
            return;
        }
        if (this.overlayId) {
            this.applicationContext.value.overlay.close(this.overlayId).catch((e) => console.warn(e));
        }
        switch (id) {
            case MenuItemIds.CopyAddress:
                this.context.value.copyAddress(this.walletAddress);
                break;
            case MenuItemIds.ExternalView:
                this.context.value.openExplorer(this.chainId, this.walletAddress);
                break;
            case MenuItemIds.Switch:
                this.onChangeWalletClick();
                break;
            case MenuItemIds.Disconnect:
                dispatchEvent(this, DisconnectEventModel.EVENT_TYPE, new DisconnectEventModel(this.walletInfo, this.walletAddress));
                break;
        }
    }
    async onMoreClick(target) {
        if (this.applicationContext.value.overlay.isOpenOverlay(this.overlayId)) {
            await this.closeMenuMore();
            return;
        }
        this.overlayId = await this.applicationContext.value.overlay.open(html `
        <inch-wallet-account-card-more
          .items="${this.menuItems}"
          @menuMoreItemClick="${(e) => this.onMenuItemClick(e.detail.value)}"
        ></inch-wallet-account-card-more>
      `, {
            mode: OverlayViewMode.popupAuto,
            targetFactory: () => target,
        });
    }
    async closeMenuMore() {
        if (this.overlayId) {
            await this.applicationContext.value.overlay.close(this.overlayId);
            this.overlayId = null;
        }
    }
    generateColorByAddress(address) {
        return getRandomBrightColor(() => Number(address) / 10 ** 48);
    }
    render() {
        const address = this.walletAddress;
        const icon = this.walletInfo?.icon;
        const name = this.walletInfo?.name;
        const hasData = address && icon && name;
        const color = address && this.generateColorByAddress(address);
        const backgroundStyle = color
            ? `--card-background: ${color}`
            : `--card-background: var(--primary-hover)`;
        return html `
      <div class="card ${this.isCollapsed ? 'collapsed' : ''}" style="${backgroundStyle}">
        <inch-icon
          class="background-unicorn ${this.isCollapsed ? 'collapsed' : ''}"
          icon="unicornBackground"
        ></inch-icon>

        <div class="card-wallet-info-container">
          <div class="card-wallet-container ${this.isCollapsed ? 'fade-out' : ''}">
            <div
              class="card-wallet ${hasData ? '' : 'loader'} ${this.isCollapsed ? 'fade-out' : ''}"
            >
              ${when(hasData, () => html `
                  <div class="card-wallet-icon">
                    <img class="wallet-icon" alt="${name}" src="${icon}" />
                  </div>
                  <inch-address-view
                    class="card-wallet-address"
                    address="${address}"
                  ></inch-address-view>

                  <inch-button
                    @click="${() => this.onChangeWalletClick()}"
                    type="tertiary"
                    size="xs"
                  >
                    <inch-icon class="card-item__color" icon="swap24"></inch-icon>
                  </inch-button>
                `)}
            </div>
          </div>

          <div class="card-wallet-full-balance ${this.isCollapsed ? 'collapsed' : ''}">
            <inch-wallet-total-fiat-balance
              class="card-wallet-balance"
              .chainIds="${observe(this.chainListView$)}"
              .address="${this.walletAddress}"
            ></inch-wallet-total-fiat-balance>
          </div>

          <inch-button
            class="card-menu-more"
            @click="${(e) => this.onMoreClick(e.target)}"
            type="tertiary"
            size="xs"
          >
            <inch-icon class="card-item__color" icon="more24"></inch-icon>
          </inch-button>
        </div>
        <div class="card-actions ${this.isCollapsed ? 'fade-out' : ''}">
          <inch-button @click="${() => { }}" type="tertiary" fullSize="${true}" size="l">
            <inch-icon class="btn-send-icon-arrow" icon="arrowLeft24"></inch-icon>
            <span class="card-item__color">
              ${translate(`widgets.wallet-account-view.card.send`)}
            </span>
          </inch-button>

          <inch-button @click="${() => { }}" type="tertiary" fullSize="${true}" size="l">
            <inch-icon class="btn-receive-icon-arrow" icon="arrowLeft24"></inch-icon>
            <span class="card-item__color">
              ${translate(`widgets.wallet-account-view.card.receive`)}
            </span>
          </inch-button>
        </div>
      </div>
    `;
    }
};
__decorate([
    state(),
    __metadata("design:type", String)
], WalletAccountCardElement.prototype, "walletAddress", void 0);
__decorate([
    state(),
    __metadata("design:type", Object)
], WalletAccountCardElement.prototype, "walletInfo", void 0);
__decorate([
    state(),
    __metadata("design:type", Number)
], WalletAccountCardElement.prototype, "chainId", void 0);
__decorate([
    state(),
    __metadata("design:type", Object)
], WalletAccountCardElement.prototype, "isCollapsed", void 0);
__decorate([
    throttle(500),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Number]),
    __metadata("design:returntype", void 0)
], WalletAccountCardElement.prototype, "onMenuItemClick", null);
__decorate([
    throttle(300),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [HTMLElement]),
    __metadata("design:returntype", Promise)
], WalletAccountCardElement.prototype, "onMoreClick", null);
WalletAccountCardElement = __decorate([
    customElement(WalletAccountCardElement.tagName)
], WalletAccountCardElement);
export { WalletAccountCardElement };
//# sourceMappingURL=wallet-account-card.element.js.map